import dayjs from 'dayjs';
import { dateStore } from '../store/dateStore';
import { useMemo } from 'react';

/**
 * 2025년 4-5월 PFGT 토너먼트 이벤트 일정
 *
 * 주요 일정:
 * - 4월 22일 ~ 28일: 새틀라이트 오픈 전 (토너먼트 상금 안내 탭만 보임)
 * - 4월 29일 ~ 5월 6일: 메인/사이드 새틀라이트 진행 중
 * - 5월 6일: 메인/사이드 새틀라이트 종료
 * - 5월 9일까지: 개인정보 동의 가능 기간
 * - 5월 16일: 사이드 이벤트 시작 (QR 코드 노출)
 * - 5월 17일 ~ 18일: 메인 이벤트 시작 (QR 코드 노출)
 */

// 새틀라이트 시작일 (4월 29일)
const SATELLITE_START_DATE = '2025-04-28T23:59:59';

// 메인 새틀라이트 종료일 (5월 6일)
const SATELLITE_END_DATE = '2025-05-07T23:59:59';

// 사이드 이벤트 시작일 (5월 2일)
const START_SIDE_EVENT = '2025-05-01T23:59:59';

// 탭 오픈일 (4월 29일) - 새틀라이트 시작과 동일
const IS_TAB_OPEN_DATE = SATELLITE_START_DATE;

// 개인정보 동의 종료일 (5월 9일)
const IS_OVER_PRIVACY_END_DATE = '2025-05-09T23:59:59';

// QR 코드 노출일 - 사이드 이벤트: 5월 16일, 메인 이벤트: 5월 17일
const SHOW_SIDE_QR_DATE = '2025-05-15T23:59:59';
const SHOW_MAIN_QR_DATE = '2025-05-16T23:59:59';

/**
 * 2025 PFGT 토너먼트 이벤트 날짜 관리 훅
 *
 * 주요 기능:
 * - 현재 날짜에 따른 이벤트 단계 확인
 * - 메인/사이드 이벤트 새틀라이트 진행 상태 확인
 * - 개인정보 동의 기간 확인
 * - QR 코드 노출 여부 확인 (메인/사이드 이벤트 별)
 *
 * 이벤트 단계:
 * - BEFORE_SATELLITE: 새틀라이트 오픈 전 (4월 22일 ~ 28일)
 * - SATELLITE_IN_PROGRESS: 새틀라이트 진행 중 (4월 29일 ~ 5월 6일)
 * - BEFORE_QR_DISPLAY: 개인정보 동의 기간 종료 ~ QR 노출 전 (5월 10일 ~ 15일)
 * - SIDE_QR_ONLY: 사이드 이벤트 QR 노출, 메인 이벤트 QR 노출 전 (5월 16일)
 * - MAIN_AND_SIDE_QR: 메인 및 사이드 이벤트 QR 노출 (5월 17일 ~ 18일)
 *
 * @returns {Object} 날짜 관련 유틸리티 함수 및 상태
 */
export const useDateHook = () => {
  const { today, setToday } = dateStore();

  // 새틀라이트 시작 여부 확인 (4월 29일 이후)
  const isStartSatellite = () => {
    const targetDate = dayjs(IS_TAB_OPEN_DATE);
    return today.isAfter(targetDate);
  };

  // 온라인 세틀라이트 종료 여부 (5월 6일 이후)
  const isSatelliteEnd = () => {
    const targetDate = dayjs(SATELLITE_END_DATE);

    return today.isAfter(targetDate);
  };

  const isStartSideEvent = () => {
    const targetDate = dayjs(START_SIDE_EVENT);
    return today.isAfter(targetDate);
  };
  // 개인정보 처리방침 동의 가능 기간 종료 여부 (5월 9일 이후)
  const isOverPrivacyDate = useMemo(() => {
    const targetDate = dayjs(IS_OVER_PRIVACY_END_DATE);
    return today.isAfter(targetDate);
  }, [today]);

  // QR코드 노출 여부 확인 - 메인 또는 사이드 이벤트에 따라 다른 일정 적용
  const showQrDate = (isMainEvent = true) => {
    const targetDate = isMainEvent
      ? dayjs(SHOW_MAIN_QR_DATE) // 5월 17일부터 (5월 16일 23:59:59 이후)
      : dayjs(SHOW_SIDE_QR_DATE); // 5월 16일부터 (5월 15일 23:59:59 이후)

    return today.isAfter(targetDate);
  };

  // 현재 날짜 설정
  const setTodayDate = () => {
    setToday(dayjs());
  };

  return {
    setTodayDate,
    isOverPrivacyDate,
    today,
    isSatelliteEnd,
    showQrDate,
    setToday,
    isStartSatellite,
    isStartSideEvent,
  };
};

// 기본 내보내기 추가
export default useDateHook;
